
import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/use-toast';

export const useFileUpload = () => {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const uploadFile = async (file: File, bucket: string, path?: string): Promise<string | null> => {
    try {
      setUploading(true);
      setUploadProgress(0);

      // Generate unique filename if path not provided
      const fileName = path || `${Date.now()}-${file.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`;
      
      console.log(`[FILE-UPLOAD] Uploading ${file.name} to ${bucket}/${fileName}`);

      const { data, error } = await supabase.storage
        .from(bucket)
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) {
        console.error('[FILE-UPLOAD] Upload failed:', error);
        throw error;
      }

      console.log('[FILE-UPLOAD] Upload successful:', data);

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from(bucket)
        .getPublicUrl(fileName);

      setUploadProgress(100);
      return publicUrl;

    } catch (error: any) {
      console.error('[FILE-UPLOAD] Error uploading file:', error);
      toast({
        title: "Upload Failed",
        description: error.message || "Failed to upload file. Please try again.",
        variant: "destructive",
      });
      return null;
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const deleteFile = async (bucket: string, path: string): Promise<boolean> => {
    try {
      console.log(`[FILE-UPLOAD] Deleting file from ${bucket}/${path}`);
      
      const { error } = await supabase.storage
        .from(bucket)
        .remove([path]);

      if (error) {
        console.error('[FILE-UPLOAD] Delete failed:', error);
        throw error;
      }

      console.log('[FILE-UPLOAD] File deleted successfully');
      return true;
    } catch (error: any) {
      console.error('[FILE-UPLOAD] Error deleting file:', error);
      toast({
        title: "Delete Failed",
        description: error.message || "Failed to delete file.",
        variant: "destructive",
      });
      return false;
    }
  };

  return {
    uploading,
    uploadProgress,
    uploadFile,
    deleteFile
  };
};
